import { PlayerGameStatus } from '@/API';
import {
  useNextGame,
  usePlayers,
  useRound,
  useSetSharingPlayer,
  useSetEnding
} from '@/hooks/HostSessionControlHooks';
import { faArrowRight, faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import AnimatedHostGame from '../Animated/Backgrounds/AnimatedHostBoardBackground';
import { ArrowTipDownIcon, ArrowTipUpIcon, RandomizeBtnIcon } from '../Icons/CommonIcons';
import HostSessionFooter from '../Navigation/Footers/HostSessionFooter';
import PlayersList from '../PlayersList/PlayersList';
import { QRCodeMiniSection, QRModal } from '../QRSection';
import PlayersCards from './PlayersCardsGame';
import OldNavMenu from '../Navigation/OldNavMenu';
import HostTips from './HostTips';
import { AnimatePresence, motion } from 'motion/react';
import ReactGA from 'react-ga4';

export default function HostGame({
  tipsEnabled,
  dismissableTips,
  onTipsDismissed
}: {
  tipsEnabled: boolean;
  dismissableTips: boolean;
  onTipsDismissed: () => void;
}) {
  // States
  const [isQRModalOpen, setIsQRModalOpen] = useState<boolean>(false);
  const [isNextQuestionButtonShown, setIsNextQuestionButtonShown] = useState<boolean>(false);
  const [isNextQuestionBtnDisabled, setIsNextQuestionBtnDisabled] = useState(false);
  const [isRandomizeBtnDisabled, setIsRandomizeBtnDisabled] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Session status
  const round = useRound();
  const players = usePlayers();
  const nextGame = useNextGame();
  const setEnding = useSetEnding();
  const setSharingPlayer = useSetSharingPlayer();

  useEffect(() => {
    if (round && !round.isLast) {
      setIsNextQuestionButtonShown(true);
    } else {
      setIsNextQuestionButtonShown(false);
    }
  }, [round]);

  const playersToSelect = players.filter(
    (player) => player.gameStatus == PlayerGameStatus.SUBMITTED
  );

  const playersSelected = players.filter(
    (player) =>
      player.gameStatus == PlayerGameStatus.SHARED || player.gameStatus == PlayerGameStatus.SHARING
  );

  /**
   * This particular piece of code is quite problematic--It is currently 100% dependent
   * on the host to successfully issue all updates to the database and players, if any
   * of these updates fail, the game will break for most players. This needs to be moved to the
   * backend and the frontend should only be responsible for displaying the game state.
   * @returns
   */
  const handleNextQuestionClick = async () => {
    try {
      setIsNextQuestionBtnDisabled(true);

      nextGame();
    } catch (error) {
      console.error('Error handling next question:', error);
    } finally {
      setIsNextQuestionBtnDisabled(false);
    }
  };

  const handleRandomSelection = async () => {
    if (isSaving) {
      return;
    }

    setIsRandomizeBtnDisabled(true);

    if (playersToSelect.length > 0) {
      const randomPlayerIndex = Math.floor(Math.random() * playersToSelect.length);
      const randomlySelectedPlayer = playersToSelect[randomPlayerIndex];

      if (!randomlySelectedPlayer) {
        console.error('Randomly selected player is undefined');
        return;
      }

      setSharingPlayer(randomlySelectedPlayer.id);
    }

    // Sleep for 2 seconds to prevent rapid randomization
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setIsRandomizeBtnDisabled(false);
  };

  const handleEnding = async () => {
    if (isSaving) {
      return;
    }

    const savingTimer = setTimeout(() => {
      setIsSaving(true);
    }, 1500);

    ReactGA.event({
      category: 'Gamme',
      action: 'Clicked Button',
      label: 'End Game'
    });

    try {
      await setEnding();
    } finally {
      clearTimeout(savingTimer);
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="h-[calc(100vh-100px)] min-h-[calc(698px-100px)] flex flex-col overflow-hidden">
        {isSaving && (
          <div className="absolute inset-0 z-40 bg-[#ddd]/50 backdrop-blur-xs flex flex-col items-center justify-center text-secondary-midnight-600 gap-4">
            <FontAwesomeIcon icon={faCircleNotch} spin className="text-3xl" />
            <div className="text-lg font-semibold tracking-wide">Saving your game...</div>
          </div>
        )}
        <div className="flex-1 relative z-10 overflow-hidden">
          <OldNavMenu includeRole={false} includeProfile={false} />
          <div className="h-[calc(100%-80px)] bg-center bg-no-repeat bg-cover w-full">
            <AnimatedHostGame />
            <div className="flex flex-row h-full">
              <div className="w-[22%] flex flex-col items-start h-full ">
                <div className="pl-64 pt-[16px] w-full h-full">
                  <PlayersList isHostGameView />
                </div>
              </div>
              <div className="w-[78%] h-full flex justify-center items-center">
                <PlayersCards />
              </div>
            </div>
          </div>
        </div>
        <div className="z-50">
          <HostSessionFooter>
            <QRModal open={isQRModalOpen} onClose={() => setIsQRModalOpen(false)} />
            <div className="flex w-full justify-between items-center">
              <div className="flex justify-center item-center relative mr-185">
                <AnimatePresence>
                  {tipsEnabled &&
                    !isSaving &&
                    playersToSelect.length > 0 &&
                    playersSelected.length < 1 && (
                      <motion.div
                        className="absolute w-474 ml-190 -top-124 flex flex-col items-end left-0"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 30 }}
                      >
                        <ArrowTipDownIcon className="-left-61 top-48 absolute" />
                        <ArrowTipUpIcon className="-right-50 absolute -top-43" />
                        <HostTips
                          text="Start spotlighting drawings now. Click a card or use “Randomize” button for more fun."
                          dismissable={dismissableTips}
                          onDismiss={onTipsDismissed}
                        />
                      </motion.div>
                    )}
                </AnimatePresence>
                <button
                  className="flex btn small bg-[#1D1A34] border-[#20145F] text-[#FFFFFF] font-bold text-lg disabled:opacity-50 transition duration-300 disabled:cursor-not-allowed"
                  disabled={isSaving || isRandomizeBtnDisabled || !playersToSelect.length}
                  onClick={handleRandomSelection}
                >
                  <RandomizeBtnIcon className="flex mr-2" />
                  Randomize
                </button>
              </div>

              <div
                className={`flex justify-center items-center transition-opacity ${
                  isSaving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer '
                }`}
                aria-disabled={isSaving}
                onClick={() => {
                  if (isSaving) return;
                  void setIsQRModalOpen(true);
                }}
              >
                <QRCodeMiniSection />
              </div>
              <div className="flex justify-end item-center relative min-w-370">
                <AnimatePresence>
                  {tipsEnabled && !isSaving && playersSelected.length > 0 && (
                    <motion.div
                      className="absolute w-448 right-0 mr-135 -top-124 flex flex-col items-end"
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 30 }}
                    >
                      <ArrowTipDownIcon className="-right-61 absolute top-41 -scale-x-100" />
                      <HostTips
                        text={
                          isNextQuestionButtonShown
                            ? 'After your discussion, click “Next Question” to continue the game.'
                            : 'After your discussion, Click ‘End Game’ to finish and see Artifacts'
                        }
                        dismissable={dismissableTips}
                        onDismiss={onTipsDismissed}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
                <button
                  className="btn small bg-white border-[3px] border-[#20145F] text-[#20145F] font-bold text-lg disabled:opacity-60"
                  onClick={handleEnding}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <FontAwesomeIcon icon={faCircleNotch} spin className="mr-10" />
                      Saving
                    </>
                  ) : (
                    'End Game'
                  )}
                </button>
                {isNextQuestionButtonShown && (
                  <button
                    className="btn small bg-[#18864A] border-[#18864A] ml-[20px] text-[#FFFFFF] font-bold text-lg disabled:opacity-60"
                    onClick={handleNextQuestionClick}
                    disabled={isNextQuestionBtnDisabled || isSaving}
                  >
                    Next Question
                    <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                  </button>
                )}
              </div>
            </div>
          </HostSessionFooter>
        </div>
      </div>
    </>
  );
}
